import type { FC } from 'react';
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HourglassIcon from '@/assets/images/desk/hourglass.svg';
import ClockIcon from '@/assets/images/desk/clock.svg';
import { DateDisplay } from './DateDisplay';
import { TimeDisplay } from './TimeDisplay';
import { LocalStorageKeys } from '@/constants/localStorage';
import { useTimePanelController } from './hooks/useTimePanelController';
import HoverMenuButton from '@/components/menu/HoverMenuButton';
import { buildPanelMenuItems } from '@/components/menu/panelMenu';
import { useUser } from '@/hooks/UserContext';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import type { TimeDisplayFont } from './fontOptions';
import { DEFAULT_FONT, FONT_OPTIONS, getFontOption } from './fontOptions';
import Select from '@/components/Select';
import { FontChangeIcon } from '@/components/icons/FontIcons';
import { FullscreenLockedModal } from '@/components/FullscreenLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';

type TimeClockPanelProps = {
    onEnterFullScreen?: () => void;
    onExitFullScreen?: () => void;
    isFullScreen?: boolean;
};

/**
 * 時間表示パネル
 */
const TimeClockPanel: FC<TimeClockPanelProps> = ({
    onEnterFullScreen,
    onExitFullScreen,
    isFullScreen = false,
}) => {
    const { t } = useTranslation('timePanel');
    const { t: tCommon } = useTranslation('common');
    const { userInfo } = useUser();
    const isPaidPlan = userInfo?.isPaid === true;
    const [isFullscreenLockedModalOpen, setIsFullscreenLockedModalOpen] = useState(false);
    const [selectedFont, setSelectedFont] = useState<TimeDisplayFont>(DEFAULT_FONT);

    const {
        containerRef,
        fontSize,
        isCountdown,
        toggleTimeMode,
        now,
        currentHour,
        currentMinute,
        currentSecond,
    } = useTimePanelController({
        storageKey: LocalStorageKeys.TIME_COUNTDOWN_STATE,
        fontSizeCoefficient: 16,
    });

    // フォント設定を読み込み
    useEffect(() => {
        if (!userInfo?.userId) return;
        let cancelled = false;

        const loadFont = async () => {
            try {
                const savedFont = await getLocalStorage(
                    userInfo.userId,
                    LocalStorageKeys.TIME_DISPLAY_FONT,
                );
                if (savedFont && !cancelled) {
                    const font = savedFont as string;
                    // 既存の設定との互換性: 'orbitron'や'wallpoet'は'normal'にマッピング
                    let mappedFont: TimeDisplayFont = DEFAULT_FONT;
                    if (font === 'orbitron' || font === 'wallpoet') {
                        mappedFont = 'normal';
                    } else if (
                        font === 'digital-7' ||
                        font === 'digital-7-dark' ||
                        font === 'digital-7-max' ||
                        font === 'digital-7-max-dark' ||
                        font === 'normal'
                    ) {
                        mappedFont = font as TimeDisplayFont;
                    }
                    if (FONT_OPTIONS.some((opt) => opt.value === mappedFont)) {
                        setSelectedFont(mappedFont);
                    }
                }
            } catch (error) {
                console.error('フォント設定の読み込みエラー:', error);
            }
        };

        void loadFont();

        return () => {
            cancelled = true;
        };
    }, [userInfo?.userId]);

    // フォント変更ハンドラー
    const handleFontChange = useCallback(
        async (option: { value: string; label: string }) => {
            const font = option.value as TimeDisplayFont;
            setSelectedFont(font);
            if (userInfo?.userId) {
                try {
                    await setLocalStorage(
                        userInfo.userId,
                        LocalStorageKeys.TIME_DISPLAY_FONT,
                        font,
                    );
                } catch (error) {
                    console.error('フォント設定の保存エラー:', error);
                }
            }
        },
        [userInfo?.userId],
    );

    const toggleLabel = isCountdown ? t('toggle.toNormal') : t('toggle.toCountdown');
    const iconAlt = isCountdown ? t('toggle.clockAlt') : t('toggle.hourglassAlt');

    const fontOption = getFontOption(selectedFont);
    const selectOptions = FONT_OPTIONS.map((opt) => {
        let label = opt.label;
        // フォントオプションのラベルを翻訳
        if (opt.value === 'normal') {
            label = t('font.options.normal');
        } else if (opt.value === 'digital-7') {
            label = t('font.options.digital');
        } else if (opt.value === 'digital-7-dark') {
            label = t('font.options.digitalDark');
        } else if (opt.value === 'digital-7-max') {
            label = t('font.options.digitalMax');
        } else if (opt.value === 'digital-7-max-dark') {
            label = t('font.options.digitalMaxDark');
        }
        return {
            value: opt.value,
            label,
        };
    });

    // 選択中のフォントに対応する翻訳済みオプションを取得
    const selectedOption =
        selectOptions.find((opt) => opt.value === selectedFont) || selectOptions[0];

    // メニュー項目を構築
    const renderFontChangeIcon = () => <FontChangeIcon className="w-4 h-4" />;
    const menuItems = buildPanelMenuItems({
        baseItems: [
            {
                key: 'toggle',
                label: toggleLabel,
                icon: isCountdown ? (
                    <img src={ClockIcon} alt={iconAlt} className="filter invert-[0.5] w-4 h-4" />
                ) : (
                    <img
                        src={HourglassIcon}
                        alt={iconAlt}
                        className="filter invert-[0.5] w-4 h-4"
                    />
                ),
                onClick: toggleTimeMode,
            },
            {
                key: 'font-select',
                label: t('font.modeChange'),
                icon: renderFontChangeIcon(),
                customRender: (onClose: () => void) => (
                    <div
                        className="w-full flex items-center gap-2"
                        style={{ fontSize: '0.875rem' }}
                    >
                        {renderFontChangeIcon()}
                        <div className="flex-1">
                            <Select
                                options={selectOptions}
                                defaultValue={selectedOption}
                                onValueChange={(option) => {
                                    handleFontChange(option);
                                    onClose();
                                }}
                                className="w-full"
                                dropdownClassName="w-full"
                                noBorder={true}
                            />
                        </div>
                    </div>
                ),
            },
        ],
        enableFullscreen: true,
        isFullScreen,
        onEnterFullScreen: isPaidPlan ? onEnterFullScreen : undefined,
        onExitFullScreen,
        enterLabel: tCommon('actions.fullscreenEnter'),
        exitLabel: tCommon('actions.fullscreenExit'),
        isFullscreenLocked: !isPaidPlan,
        fullscreenTooltip: tCommon('actions.fullscreenLockedTooltip'),
        onFullscreenLockedClick: () => setIsFullscreenLockedModalOpen(true),
    });
    const isDark = fontOption.isDark === true;
    const panelShadowRef = useRef<HTMLElement | null>(null);
    const fullscreenContentRef = useRef<HTMLElement | null>(null);

    /** グリッド高さ1行程度：日付と時刻を横並び用 */
    const [layoutCompact, setLayoutCompact] = useState(false);
    const [compactPanelHeight, setCompactPanelHeight] = useState(0);
    const [compactPanelWidth, setCompactPanelWidth] = useState(0);
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const measure = () => {
            const h = el.getBoundingClientRect().height;
            const w = el.getBoundingClientRect().width;
            const compact = h > 0 && h < 92;
            setLayoutCompact(compact);
            setCompactPanelHeight(compact ? h : 0);
            setCompactPanelWidth(compact ? w : 0);
        };
        measure();
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [containerRef]);

    /** 横長コンパクト時：パネル横幅の約9割を表示に使う想定でスケール */
    const widthBudgetPx =
        layoutCompact && compactPanelWidth > 0 ? compactPanelWidth * 0.9 : 0;
    const heightBudgetPx =
        layoutCompact && compactPanelHeight > 0 ? compactPanelHeight * 0.9 : 0;

    // 時刻と日付の幅の比率からフォントサイズを計算
    // timeFontSize * 6.565 ≒ widthBudgetPx
    const timeFontByWidth = widthBudgetPx / 6.5;
    const dateFontByWidth = timeFontByWidth * 0.45;

    const timeFontSize = !layoutCompact
        ? fontSize
        : widthBudgetPx > 0
          ? Math.max(Math.min(timeFontByWidth, heightBudgetPx, 80), 14)
          : Math.max(fontSize * 1.22, 12);

    const dateFontSize = !layoutCompact
        ? fontSize
        : widthBudgetPx > 0
          ? Math.max(Math.min(dateFontByWidth, heightBudgetPx / 1.55, 56), 10)
          : Math.max(fontSize * 0.65, 8);

    const compactRootFontPx =
        layoutCompact && widthBudgetPx > 0 ? dateFontSize : fontSize;

    // 通常時の親要素（panel-shadow）のスタイルを制御
    useEffect(() => {
        if (!containerRef.current) return;

        const findPanelShadow = () => {
            let element: HTMLElement | null = containerRef.current;
            while (element && element.parentElement) {
                if (element.parentElement.classList.contains('panel-shadow')) {
                    return element.parentElement;
                }
                element = element.parentElement;
            }
            return null;
        };

        const panelShadow = findPanelShadow();
        if (panelShadow) {
            panelShadowRef.current = panelShadow;
            if (isDark && !isFullScreen) {
                // ダークモードかつ通常時は親要素の背景も黒にする
                panelShadow.style.backgroundColor = '#000000';
            } else {
                // リセット
                panelShadow.style.backgroundColor = '';
            }
        }

        return () => {
            if (panelShadowRef.current) {
                panelShadowRef.current.style.backgroundColor = '';
            }
        };
    }, [isDark, isFullScreen, containerRef]);

    // フルスクリーン時の親要素のスタイルを制御
    useEffect(() => {
        if (!isFullScreen || !isDark) {
            // フルスクリーンでない、またはダークモードでない場合はリセット
            if (fullscreenContentRef.current) {
                fullscreenContentRef.current.style.background = '';
                fullscreenContentRef.current.style.padding = '';
            }
            return;
        }

        // 親要素（panel-fullscreen-content）を探す
        const findFullscreenContent = () => {
            let element: HTMLElement | null = containerRef.current;
            while (element && element.parentElement) {
                if (element.parentElement.classList.contains('panel-fullscreen-content')) {
                    return element.parentElement;
                }
                element = element.parentElement;
            }
            return null;
        };

        const fullscreenContent = findFullscreenContent();
        if (fullscreenContent) {
            fullscreenContentRef.current = fullscreenContent;
            fullscreenContent.style.background = '#000000';
            fullscreenContent.style.padding = '0';
        }

        return () => {
            if (fullscreenContentRef.current) {
                fullscreenContentRef.current.style.background = '';
                fullscreenContentRef.current.style.padding = '';
            }
        };
    }, [isFullScreen, isDark, containerRef]);

    return (
        <div
            ref={containerRef}
            style={{
                fontSize: `${compactRootFontPx}px`,
                backgroundColor: isDark ? '#000000' : undefined,
                margin: isDark && !isFullScreen ? '-8px' : undefined,
                borderRadius: isDark && !isFullScreen ? '0.5rem' : undefined,
                overflow: isDark && !isFullScreen ? 'hidden' : undefined,
                padding: isDark && !isFullScreen ? '8px' : undefined,
            }}
            className={`w-full h-full flex relative min-h-0 ${
                layoutCompact
                    ? 'flex-row flex-nowrap items-center justify-center gap-2 min-w-0 px-2'
                    : 'flex-col justify-center items-center gap-2'
            }`}
        >
            {/* メニューボタン */}
            <div
                className={`absolute ${isFullScreen ? 'z-[2100]' : 'z-[100]'} ${
                    layoutCompact ? 'top-0.5 right-0.5' : 'top-0 right-0'
                }`}
                style={{ overflow: 'visible' }}
            >
                <HoverMenuButton title={toggleLabel} ariaLabel={toggleLabel} items={menuItems} />
            </div>
            <DateDisplay now={now} font={selectedFont} fontSize={dateFontSize} compact={layoutCompact} />
            <TimeDisplay
                isCountdown={isCountdown}
                currentHour={currentHour}
                currentMinute={currentMinute}
                currentSecond={currentSecond}
                font={selectedFont}
                fontSize={timeFontSize}
                compact={layoutCompact}
            />
            {/* フルスクリーンロック案内モーダル */}
            {!isPaidPlan && (
                <FullscreenLockedModal
                    isOpen={isFullscreenLockedModalOpen}
                    onClose={() => setIsFullscreenLockedModalOpen(false)}
                    onGoPlans={() => {
                        openPlansWithTracking({
                            source: 'fullscreen_timeClock',
                            panelId: 'hmTimeClock',
                        });
                    }}
                />
            )}
        </div>
    );
};

export default TimeClockPanel;
